import { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'

// Picks a project from a long list: type to narrow it (code or name, accents and case aside), the list is in code
// order with the name beside it in its own column. The value is the project's id, as a string like a <select>.
const sinTildes = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
const porCodigo = (a, b) => {
  if (!a.code && !b.code) return (a.name || '').localeCompare(b.name || '')
  if (!a.code) return 1
  if (!b.code) return -1
  return a.code.localeCompare(b.code, undefined, { numeric: true }) || (a.name || '').localeCompare(b.name || '')
}

export default function SelectorProyecto({ proyectos, value, onChange, className = '', placeholder = '', textoVacio = '—', ...resto }) {
  const [texto, setTexto] = useState('')
  const [abierto, setAbierto] = useState(false)
  const [activo, setActivo] = useState(0)
  const [caja, setCaja] = useState(null)
  const ref = useRef(null)

  const ordenados = useMemo(() => [...(proyectos || [])].sort(porCodigo), [proyectos])
  const elegido = ordenados.find(p => String(p.id) === String(value))
  const etiqueta = elegido ? `${elegido.code ? `${elegido.code}  ·  ` : ''}${elegido.name}` : ''

  const visibles = useMemo(() => {
    const q = sinTildes(texto).trim()
    if (!q) return ordenados
    return ordenados.filter(p => sinTildes(p.code).includes(q) || sinTildes(p.name).includes(q))
  }, [ordenados, texto])

  const medir = useCallback(() => {
    const r = ref.current?.getBoundingClientRect()
    if (r) setCaja({ left: r.left, top: r.bottom + 2, width: Math.max(r.width, 420) })
  }, [])

  useEffect(() => {
    if (!abierto) return undefined
    window.addEventListener('scroll', medir, true)
    window.addEventListener('resize', medir)
    return () => { window.removeEventListener('scroll', medir, true); window.removeEventListener('resize', medir) }
  }, [abierto, medir])

  const abrir = () => { setTexto(''); setActivo(0); medir(); setAbierto(true) }
  const elegir = (p) => { onChange(p ? String(p.id) : ''); setAbierto(false); setTexto('') }

  const teclas = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); if (!abierto) abrir(); else setActivo(a => Math.min(visibles.length - 1, a + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActivo(a => Math.max(0, a - 1)) }
    else if (e.key === 'Enter' && abierto) { e.preventDefault(); if (visibles[activo]) elegir(visibles[activo]) }
    else if (e.key === 'Escape' && abierto) { e.stopPropagation(); setAbierto(false); setTexto('') }
  }

  return (
    <>
      <input ref={ref} className={className} autoComplete="off" role="combobox" aria-expanded={abierto}
        value={abierto ? texto : etiqueta} placeholder={abierto ? (etiqueta || placeholder) : placeholder}
        onFocus={abrir} onClick={() => { if (!abierto) abrir() }}
        onChange={e => { setTexto(e.target.value); setActivo(0); if (!abierto) { medir(); setAbierto(true) } }}
        onBlur={() => setTimeout(() => { setAbierto(false); setTexto('') }, 150)} onKeyDown={teclas} {...resto} />
      {abierto && caja && createPortal(
        <div style={{ position: 'fixed', left: caja.left, top: caja.top, width: caja.width, zIndex: 60 }}
          className="max-h-80 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg text-sm">
          <div className="sticky top-0 grid grid-cols-[6.5rem_1fr] gap-3 px-3 py-1.5 bg-gray-50 border-b border-gray-100 text-[11px] font-medium uppercase tracking-wide text-gray-500">
            <span>Código</span><span>Proyecto</span>
          </div>
          {value !== '' && value != null && (
            <div onMouseDown={e => { e.preventDefault(); elegir(null) }} className="px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50 cursor-pointer">{textoVacio}</div>
          )}
          {visibles.length === 0 && <div className="px-3 py-3 text-xs text-gray-400">Sin coincidencias</div>}
          <ul role="listbox">
            {visibles.map((p, i) => (
              <li key={p.id} role="option" aria-selected={String(p.id) === String(value)}
                onMouseDown={e => { e.preventDefault(); elegir(p) }} onMouseEnter={() => setActivo(i)}
                ref={el => { if (el && i === activo) el.scrollIntoView({ block: 'nearest' }) }}
                className={`grid grid-cols-[6.5rem_1fr] gap-3 px-3 py-1.5 cursor-pointer ${i === activo ? 'bg-indigo-50' : ''} ${String(p.id) === String(value) ? 'font-medium' : ''}`}>
                <span className="font-mono text-xs text-gray-600 pt-0.5">{p.code || '—'}</span>
                <span className="text-gray-900">{p.name}</span>
              </li>
            ))}
          </ul>
        </div>, document.body)}
    </>
  )
}
